import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import FilterDrawer from '../components/FilterDrawer';
import { Edit, Trash2, Plus, Grid3x3, List, Search, SlidersHorizontal } from 'lucide-react';
import { getFirstImage } from '../utils/imageHelper';
import ListingImage from '../components/ListingImage';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { getListingTabs, filterListingsByTab, getSortingOptions, sortListings } from '../utils/listingTabsConfig';
import { getListingFilters, applyListingFilters } from '../utils/listingFiltersConfig';
import { getSubscriptionPrice, getSubscriptionCycleSuffix } from '../utils/subscriptionPrice';

const Subscriptions = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { apiCall } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('updated_desc');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({ selectedFilters: {}, rangeValues: {} });

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      const data = await apiCall('/api/listings/?listing_type=Subscriptions');
      setSubscriptions(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load subscriptions',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this subscription?')) return;
    
    try {
      await apiCall(`/api/listings/${id}`, { method: 'DELETE' });
      setSubscriptions(subscriptions.filter((s) => s.id !== id));
      toast({
        title: 'Success',
        description: 'Subscription deleted successfully'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete subscription',
        variant: 'destructive'
      });
    }
  };

  // Get filtered and sorted subscriptions
  const getDisplaySubscriptions = () => {
    let filtered = subscriptions;

    // Apply tab filter
    filtered = activeTab === 'all' ? filtered : filterListingsByTab(filtered, activeTab);

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(s => 
        s.title?.toLowerCase().includes(query) ||
        s.description?.toLowerCase().includes(query) ||
        s.category?.toLowerCase().includes(query)
      );
    }

    // Apply filters from drawer
    filtered = applyListingFilters(filtered, appliedFilters, 'Subscriptions');

    // Apply sorting
    return sortListings(filtered, sortBy);
  };

  const displaySubscriptions = getDisplaySubscriptions();
  const tabs = getListingTabs(subscriptions);
  const activeFiltersCount = Object.values(appliedFilters.selectedFilters).flat().length + 
    Object.keys(appliedFilters.rangeValues).length;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Loading subscriptions...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/listings')}
                  className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-800"
                  title="Back to All Listings"
                  data-testid="back-to-listings-btn"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>
                </button>
                <h1 className="text-3xl font-bold text-gray-900">Subscriptions</h1>
              </div>
            <p className="text-gray-600 mt-2">Manage subscription-based offerings</p>
          </div>
          <div className="flex gap-2">
            <div className="flex border rounded-lg">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
              >
                <Grid3x3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
            <Button onClick={() => navigate('/listings/subscriptions/new')} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Subscription
            </Button>
          </div>
        </div>

        {/* Tabs, Search, Filters, Sorting */}
        <Card>
          <CardContent className="p-4 md:p-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mb-4">
              <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
                <TabsList className="inline-flex md:grid md:grid-cols-6 md:w-full md:max-w-4xl gap-1 min-w-max md:min-w-0">
                  {tabs.map(tab => (
                    <TabsTrigger key={tab.id} value={tab.id} className="flex items-center gap-1 text-xs md:text-sm whitespace-nowrap px-3 py-2">
                      <tab.icon className="h-3 w-3 md:h-4 md:w-4" />
                      {tab.label} ({tab.count})
                    </TabsTrigger>
                  ))}
                  <TabsTrigger value="all" className="flex items-center gap-1 text-xs md:text-sm whitespace-nowrap px-3 py-2">
                    All ({subscriptions.length})
                  </TabsTrigger>
                </TabsList>
              </div>
            </Tabs>

            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search subscriptions by title, description, category..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              <Button 
                variant="outline" 
                onClick={() => setIsFilterOpen(true)}
                className="flex items-center gap-2"
              >
                <SlidersHorizontal className="h-4 w-4" />
                Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}
              </Button>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  {getSortingOptions().map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Filter Drawer */}
        <FilterDrawer
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          filters={getListingFilters(subscriptions, 'Subscriptions')}
          onApplyFilters={setAppliedFilters}
          activeFiltersCount={activeFiltersCount}
        />

        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displaySubscriptions.map((subscription) => (
              <Card key={subscription.id} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/listings/subscriptions/${subscription.id}`)}>
                <ListingImage
                  src={getFirstImage(subscription.media)}
                  alt={subscription.title}
                  className="w-full h-48 object-cover"
                  placeholderContent={<span className="text-gray-400">No image</span>}
                />
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-gray-900 text-lg">{subscription.title}</h3>
                    <Badge variant={subscription.status === 'active' ? 'default' : 'secondary'}>
                      {subscription.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {subscription.description}
                  </p>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      {(() => {
                        const p = getSubscriptionPrice(subscription);
                        const suffix = getSubscriptionCycleSuffix(subscription);
                        return (
                          <>
                            <span className="text-2xl font-bold text-gray-900">{p ? `₹${p.toLocaleString('en-IN')}` : '₹N/A'}</span>
                            <span className="text-sm text-gray-600 ml-1">{suffix || '/mo'}</span>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-2">
                      <Badge variant="outline">{subscription.category}</Badge>
                      {subscription.data?.autoRenewal && (
                        <Badge className="bg-blue-600 text-white">
                          Auto-Renewal
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/listings/subscriptions/edit/${subscription.id}`); }}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleDelete(subscription.id); }}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : displaySubscriptions.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">No subscriptions found</p>
            <Button onClick={() => navigate('/listings/subscriptions/new')}>
              <Plus className="h-4 w-4 mr-2" />
              Create your first subscription
            </Button>
          </div>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="p-4 text-left text-sm font-medium text-gray-600">Title</th>
                    <th className="p-4 text-left text-sm font-medium text-gray-600">Category</th>
                    <th className="p-4 text-left text-sm font-medium text-gray-600">Price</th>
                    <th className="p-4 text-left text-sm font-medium text-gray-600">Cycle</th>
                    <th className="p-4 text-left text-sm font-medium text-gray-600">Status</th>
                    <th className="p-4 text-left text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displaySubscriptions.map((subscription) => (
                    <tr key={subscription.id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/listings/subscriptions/${subscription.id}`)}>
                      <td className="p-4 font-medium">{subscription.title}</td>
                      <td className="p-4">
                        <div className="flex flex-col gap-1">
                          <Badge variant="outline">{subscription.category}</Badge>
                          {subscription.data?.autoRenewal && (
                            <Badge className="bg-blue-600 text-white text-xs">
                              Auto-Renewal
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-4 font-semibold">{(() => {
                        const p = getSubscriptionPrice(subscription);
                        const suffix = getSubscriptionCycleSuffix(subscription) || '/mo';
                        return p ? `₹${p.toLocaleString('en-IN')}${suffix}` : '₹N/A';
                      })()}</td>
                      <td className="p-4">{subscription.data?.delivery_frequency || subscription.data?.deliveryFrequency || 'N/A'}</td>
                      <td className="p-4">
                        <Badge variant={subscription.status === 'active' ? 'default' : 'secondary'}>
                          {subscription.status}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/listings/subscriptions/edit/${subscription.id}`); }}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(subscription.id); }}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Subscriptions;
